import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Button from "components/Button";
import Loading from "components/Loading";
import Modal from "components/Modal";
import Title from "components/Title";
import CardLayout from "components/form/CardLayout";
import FormLayout from "components/form/FormLayout";
import TwoSideLayout from "components/form/TwoSideLayout";
import GalleryInputMulti from "components/gallery/GalleryInputMulti";
import GalleryModal from "components/gallery/GalleryModal";
import FieldWithTranslation from "components/inputs/FieldWithTranslation";
import apiEndpoints from "configs/apiEndPoints";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toastFunc from "utils/toast";
import { transformFieldWithTranslationForServer, transformFieldWithTranslationFromServer } from "utils/transformerFunctions";
import { useMutationCustom, useQueryCustom } from "utils/useQueryCUstom";
import { CONFIGS } from "../configs";
import { fieldNames, validation } from "./validation";
import GalleryInput from "components/gallery/GalleryInput";
import TextInput from "components/inputs/TextInput";

interface FormValues {
  image?: any;
  listOrderNumber: number;
}

const Add: FC = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const URL_ID = id ? id : "";
  const [editInitialLoading, setEditInitialLoading] = useState(id ? true : false);
  const { t } = useTranslation();

  const updateOrCreate = URL_ID ? (params: any) => axios.put(apiEndpoints[CONFIGS.apiName].base + "/" + URL_ID, params) : (params: any) => axios.post(apiEndpoints[CONFIGS.apiName].base, params);

  const methods = useForm<FormValues>({
    resolver: yupResolver(validation(URL_ID, t)),
    shouldFocusError: true,
  });
  // --------------------------------------------------- language data
  const { data: languageData, isLoading: languageIsLoading } = useQueryCustom({
    name: `${CONFIGS.title}_language`,
    url: () => axios.get(apiEndpoints.languages.base),
    enabled: !!URL_ID,
  });
  // --------------------------------------------------- data by id query
  const onSuccessDataById = ({ data }: any) => {
    let params = { ...data?.data };

    handleImageThumbnail(params?.image);
    methods.reset({
      ...params,
    });
    setEditInitialLoading(false);
  };
  const { data: dataById } = useQueryCustom({
    name: `${CONFIGS.title}_getById`,
    url: () => axios.get(apiEndpoints[CONFIGS.apiName].base + "/" + URL_ID),
    onSuccess: onSuccessDataById,
    enabled: !!URL_ID && !languageIsLoading,
  });
  // --------------------------------------------------- Create or update mutation (base on URL_ID)
  const { isLoading, mutate: submitData } = useMutationCustom({
    url: updateOrCreate,
    invalidQuery: `${CONFIGS.title}_get`,
    onSuccess: () => {
      toastFunc.success({ title: "Success" });
      navigate(CONFIGS.routes.base);
    },
  });

  // -------------------------------------------- onSubmit
  const onSubmit = (data: FormValues) => {
    const requestData = {
      listOrderNumber: data?.listOrderNumber,
      image: data?.image?.path ? data?.image?.path : imageThumbnail,
    };
    console.log(requestData);
    submitData(requestData);
  };

  // -------------------------------- gallery sample ----------------------------------
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageThumbnail, setImageThumbnail] = useState<
    | {
        mimetype: string;
        src: string;
      }
    | string
    | null
  >(null);

  const handleGalleryModal = () => {
    setShowImageModal(!showImageModal);
  };

  const handleImageThumbnail = (thumbnail: { mimetype: string; src: string } | string) => {
    setImageThumbnail(thumbnail);
    methods.setValue(fieldNames.image, thumbnail);
    methods.clearErrors(fieldNames.image);
  };

  return (
    <div>
      {editInitialLoading && (
        <Modal isOpen={true} title={t("loadingData")}>
          <div className="flex justify-center items-center">
            <Loading size="w-8 h-8" />
          </div>
        </Modal>
      )}
      <Title
        hasBtn
        btnTitle={t("backTitle", { changeablePart: t(CONFIGS.title) })}
        title={URL_ID ? t("editTitle", { changeablePart: t(CONFIGS.title) }) : t("addNew", { changeablePart: t(CONFIGS.title) })}
        url={CONFIGS.routes.base + CONFIGS.routes.list}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
          <FormLayout
            main={
              <>
                <CardLayout>
                  <TextInput isRequired type="number" name={fieldNames.listOrderNumber} placeholder={t("enterYourHere", { changeablePart: t(fieldNames.listOrderNumber) })} label={fieldNames.listOrderNumber} size="full" />
                  <GalleryInput isRequired handleModal={handleGalleryModal} selectedPhoto={imageThumbnail} label={fieldNames.image} name={fieldNames.image} size="full" />
                </CardLayout>
              </>
            }
            side={
              <CardLayout head={{ title: "action" }} className="sticky top-4">
                <Button type="submit" variant="success" isFullSize loading={isLoading}>
                  {t("submit")}
                </Button>
              </CardLayout>
            }
          />
        </form>
      </FormProvider>
      <GalleryModal showModal={showImageModal} handleModal={handleGalleryModal} handleClickedImage={handleImageThumbnail} selectedPhoto={imageThumbnail} FOLDER_SLUG="your-folder-slug" />
    </div>
  );
};

export default Add;
